import { toCamelCase, firstToLowerCase } from './string';
/** The name of the module */
export const name = 'Dom';
export var Sibling;
(function (Sibling) {
    Sibling["Next"] = "next";
    Sibling["Previous"] = "previous";
})(Sibling || (Sibling = {}));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isSibling = (value) => Object.values(Sibling).includes(value);
/**
 * Find a next or previous sibling of a specified element matching a selector
 *
 * @param {HTMLElement} element - DOM element to get sibling of
 * @param {String} direction - NEXT_SIBLING or PREVIOUS_SIBLING constant specifying direction to search for sibling
 * @param {String} selector - CSS selector to match against
 *
 * @return {?HTMLElement}
 */
function getMatchingSibling(element, direction, selector) {
    // Use either the 'nextElementSibling' or 'previousElementSibling' property
    const prop = `${direction}ElementSibling`;
    // Get the first element matching the prop above
    let sibling = element[prop];
    // Loop through siblings until we find one that matches
    while (sibling && !sibling.matches(selector)) {
        sibling = sibling[prop];
    }
    // If the sibling element matches the provided selector then return it
    return sibling.matches(selector) ? sibling : null;
}
/**
 * Get a next or previous sibling of a specified element, optionally filtering by a provided selector
 *
 * @param {HTMLElement} element - DOM element to get sibling of
 * @param {String} direction - NEXT_SIBLING or PREVIOUS_SIBLING constant specifying direction to search for sibling
 * @param {String} [selector] - CSS selector to match against
 *
 * @return {?HTMLElement}
 */
export function getSibling(element, direction, selector = '') {
    // If a selector is provided, then return an element matching the selector in the specified direction, otherwise
    // just return the sibling in the specified direction
    if (direction === Sibling.Next) {
        return selector ? getMatchingSibling(element, Sibling.Next, selector) : element.nextElementSibling;
    }
    if (direction === Sibling.Previous) {
        return selector ? getMatchingSibling(element, Sibling.Previous, selector) : element.previousElementSibling;
    }
    // No match found, so return nothing
    return null;
}
/**
 * Get all siblings for a specified element, optionally returning only next or previous siblings and optionally
 * filtering by a provided selector
 *
 * @param {HTMLElement} element - DOM element to get siblings of
 * @param {String} [direction] - NEXT_SIBLING or PREVIOUS_SIBLING constant specifying direction to search for siblings
 * @param {String} [selector] - CSS selector match against
 */
export function getSiblings(element, direction = null, selector = '') {
    const siblings = [];
    // If a direction is provided, otherwise fetch all siblings
    if (isSibling(direction)) {
        // Get the first sibling in the given direction
        let sibling = getSibling(element, direction);
        while (sibling) {
            // Make sure the sibling is an element, and if there's a selector then make sure it matches the
            // provided selector
            if (sibling.nodeType === Node.ELEMENT_NODE && (!selector || (selector && sibling.matches(selector)))) {
                siblings.push(sibling);
            }
            // Get the next sibling to continue looping
            sibling = getSibling(sibling, direction);
        }
    }
    else {
        let sibling = element.parentElement.firstElementChild;
        while (sibling) {
            // Make sure the sibling is an element, isn't the reference element, and if there's a selector then make
            // sure it matches the provided selector
            if (sibling.nodeType === Node.ELEMENT_NODE && !element.isSameNode(sibling) && (!selector || (selector && sibling.matches(selector)))) {
                siblings.push(sibling);
            }
            // Get the next sibling to continue looping
            sibling = getSibling(sibling, Sibling.Next);
        }
    }
    return siblings;
}
/**
 * Finds the previous element siblings for a specified element, optionally filtered by a CSS selector
 *
 * @param {HTMLElement} element - DOM element to get previous siblings of
 * @param {String} [selector] - CSS selector for filtering
 *
 * @return {HTMLElement[]}
 */
export function getPreviousSiblings(element, selector = '') {
    return getSiblings(element, Sibling.Previous, selector);
}
/**
 * Finds the previous element sibling for a specified element, optionally filtered by a CSS selector
 *
 * @param {HTMLElement} element - DOM element to get previous siblings of
 * @param {String} [selector] - CSS selector for filtering
 *
 * @return {HTMLElement}
 */
export function getPreviousSibling(element, selector = '') {
    return getSibling(element, Sibling.Previous, selector);
}
/**
 * Finds the next element siblings for a specified element, optionally filtered by a CSS selector
 *
 * @param {HTMLElement} element - DOM element to get previous siblings of
 * @param {String} [selector] - CSS selector for filtering
 *
 * @return {HTMLElement[]}
 */
export function getNextSiblings(element, selector = '') {
    return getSiblings(element, Sibling.Next, selector);
}
/**
 * Finds the next element sibling for a specified element, optionally filtered by a CSS selector
 *
 * @param {HTMLElement} element - DOM element to get previous sibling of
 * @param {String} [selector] - CSS selector for filtering
 */
export function getNextSibling(element, selector = '') {
    return getSibling(element, Sibling.Next, selector);
}
/**
 * Finds the first element sibling for a specified element, optionally filtered by a CSS selector
 *
 * @param {HTMLElement} element - DOM element to get first sibling of
 * @param {String} [selector] - CSS selector for filtering
 *
 * @return {HTMLElement}
 */
export function getFirstSibling(element, selector = '') {
    // Start with the first element child of the provided element's parent node, then if a selector for filtering was
    // provided, walk through the siblings until finding an element that matches
    const sibling = element.parentElement.firstElementChild;
    return selector && !sibling.matches(selector) ? getNextSibling(sibling, selector) : sibling;
}
/**
 * Finds the last element sibling for a specified element, optionally filtered by a CSS selector
 *
 * @param {HTMLElement} element - DOM element to get first sibling of
 * @param {String} [selector] - CSS selector for filtering
 *
 * @return {HTMLElement}
 */
export function getLastSibling(element, selector = '') {
    // Start with the first element child of the provided element's parent node, then if a selector for filtering was
    // provided, walk backwards through the siblings until finding an element that matches
    const sibling = element.parentElement.lastElementChild;
    return selector && !sibling.matches(selector) ? getPreviousSibling(sibling, selector) : sibling;
}
/**
 * Test if an element is currently visible in the DOM
 *
 * @param element Element to test visibility of
 *
 * @return {bool}
 */
export function isElementVisible(element) {
    return element.offsetParent !== null;
}
/**
 * Return the values of a data attribute for the provided list of elements
 *
 * @param elements Collection of HTML elements to retrieve data attributes on
 * @param attribute Attribute name (minus 'data-') to be retrieved
 * @param removeEmpty Whether to remove empty values
 */
export function getDataAttributeValues(elements, attribute, removeEmpty = false) {
    // Fetch the data attribute values
    const values = elements.map((element) => element.dataset[toCamelCase(attribute)]);
    // Optionally filter out empty string values
    return removeEmpty ? values.filter((n) => n && n.length) : values;
}
/**
 * Get a key/value list of data attributes that begin with a provided string
 *
 * @param element HTMLElement to pull data attributes from
 * @param filter Value used to filter data attributes
 */
export const getFilteredDataAttributeValues = (element, filter) => {
    const values = {};
    for (const key in element.dataset) {
        if (typeof key === 'string' && key.startsWith(filter)) {
            const attributeKey = key.replace(filter, '');
            values[firstToLowerCase(attributeKey)] = element.dataset[key];
        }
    }
    return values;
};
/**
 * Remove all children of parent node
 *
 * @param parentNode Node of which all children will be removed from
 */
export function removeAllChildren(parentNode) {
    while (parentNode.firstChild) {
        parentNode.removeChild(parentNode.firstChild);
    }
    return parentNode;
}
/**
 * Test if an element is in the DOM
 *
 * @param element Element to be tested
 */
// If the element doesn't have a parentNode, then it's not in the DOM
export const isInDom = (element) => !!element.parentNode;
export var CorsAttribute;
(function (CorsAttribute) {
    CorsAttribute["Anonymous"] = "anonymous";
    CorsAttribute["UseCredentials"] = "use-credentials";
})(CorsAttribute || (CorsAttribute = {}));
/**
 * Creates a <script> tag and injects it at the end of the <body>
 *
 * @param src
 * @param attributes
 */
export const injectScriptTag = (src, attributes = {}) => {
    const script = document.createElement('script');
    if (attributes.id != null) {
        script.id = attributes.id;
    }
    if (attributes.async != null) {
        script.async = attributes.async;
    }
    if (attributes.crossOrigin != null) {
        script.crossOrigin = attributes.crossOrigin;
    }
    if (attributes.onload != null) {
        script.onload = attributes.onload;
    }
    script.src = src;
    document.body.appendChild(script);
};
/**
 * Finds an element by querySelector and returns an attribute value from the element.
 * Returns null if element or attribute not found.
 *
 * @param querySelector
 * @param attribute
 */
export const getAttributeValue = (querySelector, attribute) => {
    const element = document.querySelector(querySelector);
    return element ? element.getAttribute(attribute) : null;
};
